import React from 'react';
import { observer } from 'mobx-react';

import { Popover, Box } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import { DateRangeCalendar } from '@mui/x-date-pickers-pro/DateRangeCalendar';

import { Button } from 'components/Button';

import { timezoneStore } from 'stores/Timezone';

import { formatMomentDate, readerDayKey, readerPeriod } from 'utils/formatters/timezone';

import { Header, ShortcutsRow, ActionsBar } from './styles';

export interface DateRangeValue {
  from: number | null;
  to: number | null;
}

interface Shortcut {
  label: string;
  getValue: () => DateRangeValue;
}

const nextTwelveMonths = (): DateRangeValue => {
  const today = readerPeriod('day');
  // Midday, so a clock change in the year between can't move the instant into a neighbouring day
  const lastDay = readerPeriod('day', dayjs(today.from).add(365, 'day').add(12, 'hour'));
  return { from: today.from, to: lastDay.to };
};

const getShortcuts = (): Shortcut[] => [
  { label: 'Today', getValue: () => readerPeriod('day') },
  { label: 'This Week', getValue: () => readerPeriod('isoWeek') },
  { label: 'This Month', getValue: () => readerPeriod('month') },
  { label: 'Next 12 Months', getValue: nextTwelveMonths },
];

const isShortcutSelected = (shortcut: Shortcut, currentValue: DateRangeValue): boolean => {
  const shortcutValue = shortcut.getValue();
  if (!currentValue.from || !currentValue.to) return false;
  return (
    readerDayKey(currentValue.from) === readerDayKey(shortcutValue.from) &&
    readerDayKey(currentValue.to) === readerDayKey(shortcutValue.to)
  );
};

// A calendar bound is the start of the day picked, so a range ending on it would lose the rest of that day
const widenToReaderDays = ({ from, to }: DateRangeValue): DateRangeValue => {
  const first = from ?? to;
  const last = to ?? from;
  if (first === null || last === null) return { from, to };
  return { from: readerPeriod('day', first).from, to: readerPeriod('day', last).to };
};

export const formatDateRange = (value: DateRangeValue): string => {
  if (!value.from && !value.to) return '';

  const fromDay = value.from ? readerDayKey(value.from) : '';
  const toDay = value.to ? readerDayKey(value.to) : '';

  if (fromDay && toDay) {
    if (fromDay === toDay) {
      return formatMomentDate(value.from, 'DD MMM, YYYY');
    }
    const sameYear = fromDay.slice(0, 4) === toDay.slice(0, 4);
    if (sameYear) {
      return `${formatMomentDate(value.from, 'DD MMM')} – ${formatMomentDate(value.to, 'DD MMM')}, ${toDay.slice(0, 4)}`;
    }
    return `${formatMomentDate(value.from, 'DD MMM, YYYY')} – ${formatMomentDate(value.to, 'DD MMM, YYYY')}`;
  }

  if (fromDay) return `From ${formatMomentDate(value.from, 'DD MMM, YYYY')}`;
  return `Until ${formatMomentDate(value.to, 'DD MMM, YYYY')}`;
};

interface DateRangePickerPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  value: DateRangeValue;
  onClose: () => void;
  onChange: (value: DateRangeValue) => void;
  clearable?: boolean;
}

const DateRangePickerPopover = ({
  open,
  anchorEl,
  value,
  onClose,
  onChange,
  clearable = true,
}: DateRangePickerPopoverProps): React.ReactElement => {
  const [tempValue, setTempValue] = React.useState<DateRangeValue>(value);

  React.useEffect(() => {
    if (open) {
      setTempValue(value);
    }
  }, [open, value]);

  const handleDateRangeChange = (newValue: DateRange<Dayjs>): void => {
    setTempValue({
      from: newValue[0]?.valueOf() ?? null,
      to: newValue[1]?.valueOf() ?? null,
    });
  };

  const handleShortcutClick = (shortcut: Shortcut): void => {
    const newValue = shortcut.getValue();
    setTempValue(newValue);
  };

  const handleOk = (): void => {
    const resolved = timezoneStore.enabled ? widenToReaderDays(tempValue) : { ...tempValue };
    if (resolved.from && !resolved.to) {
      resolved.to = dayjs(resolved.from).endOf('day').valueOf();
    }
    if (resolved.to && !resolved.from) {
      resolved.from = dayjs(resolved.to).startOf('day').valueOf();
    }
    onChange(resolved);
    onClose();
  };

  const handleClear = (): void => {
    onChange({ from: null, to: null });
    onClose();
  };

  const dateRangeValue: DateRange<Dayjs> = [
    tempValue.from ? dayjs(tempValue.from) : null,
    tempValue.to ? dayjs(tempValue.to) : null,
  ];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Box display="flex" flexDirection="column">
        <Header>
          <ShortcutsRow>
            {getShortcuts().map((shortcut) => {
              const isSelected = isShortcutSelected(shortcut, tempValue);
              return (
                <Button
                  key={shortcut.label}
                  onClick={() => handleShortcutClick(shortcut)}
                  variant="text"
                  style={{
                    color: isSelected ? '#0772E5' : '#000',
                    fontWeight: 400,
                    backgroundColor: isSelected ? 'rgba(7, 114, 229, 0.08)' : '#fff',
                  }}
                >
                  {shortcut.label}
                </Button>
              );
            })}
          </ShortcutsRow>
        </Header>

        <DateRangeCalendar
          calendars={2}
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          timezone={timezoneStore.enabled ? timezoneStore.readerZone : undefined}
          slotProps={{
            calendarHeader: {
              format: 'MMMM YYYY',
            },
          }}
          sx={{
            '& .MuiTypography-subtitle1': {
              fontSize: '16px',
              fontWeight: 500,
              color: '#000',
            },
          }}
        />

        <ActionsBar>
          {clearable && (
            <Button onClick={handleClear} color="primary">
              Clear
            </Button>
          )}
          <div>
            <Button onClick={onClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleOk} color="primary">
              OK
            </Button>
          </div>
        </ActionsBar>
      </Box>
    </Popover>
  );
};

export default observer(DateRangePickerPopover);
